import { ErrorBoundaryComponent } from "#/components/error-boundary";
import {
  getStudentSociaalHistorie,
  submitClearSociaalResultaat,
} from "#/modules/school.students/students.functions";
import { HistorieRowActions } from "#/modules/student/components/historie-row-actions";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { OverachievedIconButton } from "#/modules/util.score-status/components/score-status";
import { Badge, createDataTableColumnHelper, DataTable, Link as UiLink } from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod/v4";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { LeerjaarCombo } from "#/schema.nswag";

// Child-own params, carried by the parent table's name-column link search updater (see
// `social-results/route.tsx`) — not part of the parent's own
// `sociaalresultatenSearchSchema`. No defaults: absence is a genuine error case (see loader below),
// exactly like the old `sp.get(...)` + throw.
const historieSearchSchema = z.object({
  sociaalEmotioneelThemasReeksId: z.string().optional(),
  leerjaarCombo: apiEnum(LeerjaarCombo).optional(),
});

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/social-results/drawer/historie/$sociaalEmotioneelThemaId",
)({
  validateSearch: historieSearchSchema,
  loaderDeps: ({ search }) => ({
    sociaalEmotioneelThemasReeksId: search.sociaalEmotioneelThemasReeksId,
    leerjaarCombo: search.leerjaarCombo,
  }),
  loader: ({ deps, params }) => {
    const { sociaalEmotioneelThemasReeksId, leerjaarCombo } = deps;
    if (!sociaalEmotioneelThemasReeksId || !leerjaarCombo) {
      throw new Error("Missing required parameters");
    }
    return getStudentSociaalHistorie({
      data: { leerlingId: params.id, sociaalEmotioneelThemasReeksId, leerjaarCombo },
    });
  },
  component: SociaalEmotioneelThemaResultatenHistorie,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} />,
});

function formatDate(date: Date | string | null | undefined) {
  if (!date) return "Onbekend";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

type Historie = NonNullable<Awaited<ReturnType<typeof getStudentSociaalHistorie>>["historie"]>;
type HistorieRow = Historie["sociaalEmotioneelResultaten"][number];

const helper = createDataTableColumnHelper<HistorieRow>();

function SociaalHistorieActions({ row }: { row: HistorieRow }) {
  const { id: leerlingId } = Route.useParams();
  const clearSociaalResultaat = useServerFn(submitClearSociaalResultaat);
  const { sociaalEmotioneelResultaatId, lesId, behaaldeSociaalEmotioneelDoelen } = row;

  return (
    <HistorieRowActions
      lesId={lesId}
      clearDisabled={!sociaalEmotioneelResultaatId || behaaldeSociaalEmotioneelDoelen.length === 0}
      onClear={async () => {
        await clearSociaalResultaat({
          data: { leerlingId, sociaalEmotioneelResultaatId: sociaalEmotioneelResultaatId! },
        });
      }}
    />
  );
}

function SociaalEmotioneelThemaResultatenHistorie() {
  const { historie } = Route.useLoaderData();

  const columns = useMemo(() => {
    const themas = historie?.sociaalEmotioneelThemas ?? [];
    return helper.columns([
      helper.display({
        id: "resultaat",
        header: () => m["common.resultaat"](),
        meta: { cellClassName: "py-3" },
        cell: ({ row }) => {
          const resultaat = row.original;
          const thema = themas.find((t) => t.id === resultaat.sociaalEmotioneelThemaId);
          const behaaldCount = resultaat.behaaldeSociaalEmotioneelDoelen?.length ?? 0;
          return (
            <div className="flex items-center gap-2">
              {thema ? (
                <Badge variant="secondary" size="sm">
                  {thema.name}
                  {behaaldCount > 0 ? ` · ${behaaldCount}` : ""}
                </Badge>
              ) : (
                <span>-</span>
              )}

              {resultaat.isOverachieved && <OverachievedIconButton />}
            </div>
          );
        },
      }),
      helper.display({
        id: "datum",
        header: () => m["common.fields.date"](),
        meta: { rowTarget: true, cellClassName: "py-3 text-sm" },
        cell: ({ row }) => (
          <UiLink
            variant="secondary"
            underline
            to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
            params={(prev: Record<string, string>) => ({ ...prev, lesson: row.original.lesId! })}
          >
            <Badge variant="gray" size="sm">
              {formatDate(row.original.date)}
            </Badge>
          </UiLink>
        ),
      }),
      helper.display({
        id: "actions",
        header: "",
        meta: { action: true, headerClassName: "w-0" },
        cell: ({ row }) => <SociaalHistorieActions row={row.original} />,
      }),
    ]);
  }, [historie]);

  if (!historie) {
    return (
      <div className="px-10 pt-10 pb-10">
        <p className="text-gray-500">Geen historie gevonden.</p>
      </div>
    );
  }

  return (
    <section className="pt-10">
      <h3 className="m-0 mb-2 px-4 pr-10">{m["student.results.historie.title"]()}</h3>
      <p className="text-muted-foreground mb-4 px-4 text-sm">
        {historie.sociaalEmotioneelThemaName}
      </p>
      {historie.sociaalEmotioneelResultaten.length === 0 ? (
        <p className="text-muted-foreground px-4 text-sm">
          {m["student.results.historie.no_results"]()}
        </p>
      ) : (
        <DataTable
          className="-mr-6"
          aria-label={m["student.results.historie.title"]()}
          columns={columns}
          data={historie.sociaalEmotioneelResultaten}
        />
      )}
    </section>
  );
}
